import { ResolverTag } from '@ps/web-core-enums';
import { StarsButton as Button, Translation } from '@ps/web-shared-components';
import { createForm } from '@ps/web-shared-ui';
import { omit } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { lastValueFrom } from 'rxjs';

import ResolverLink from '@swf/components/ResolverLink/ResolverLink';
import type { Question } from '@swf/components/StarsChangePassword/StrongPwdReset/StrongPwdReset';
import StrongPwdReset from '@swf/components/StarsChangePassword/StrongPwdReset/StrongPwdReset';
import type { RequestError } from '@swf/core/errors/RequestError';
import useRequest from '@swf/core/hooks/useRequest';
import useSearchParams from '@swf/core/hooks/useSearchParams';
import type { LoginPayload } from '@swf/core/models/auth/login';
import { type Dispatch } from '@swf/core/store';
import { ServerMessageHandler } from '@swf/notification';

import ErrorMessage from '../ErrorMessage/ErrorMessage';

import {
  TXTCLI_If_you_require_further_assista_ELL_8,
  TXTCLI_Login
} from './SecurityQuestionsForm.messages.json';
import styles from './SecurityQuestionsForm.scss';

interface FormValues {
  answer0?: string;
  answer1?: string;
  ssn?: string;
}

const Form = createForm<FormValues>();

const SecurityQuestionsForm: React.FC = () => {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [serverMessage, setServerMessage] = useState<string>();
  const request = useRequest();
  const searchParams = useSearchParams<LoginPayload>();
  const { auth } = useDispatch<Dispatch>();

  const onSubmit = async (formValues: FormValues) => {
    try {
      setIsLoading(true);
      setServerMessage(undefined);
      await lastValueFrom(
        request({
          service: '/api/v1-preview/auth/strong-auth/remove',
          method: 'POST',
          body: {
            ssn: formValues.ssn,
            answer1: formValues.answer0,
            answer2: formValues.answer1,
            userId: searchParams.userId
          }
        })
      );

      await auth.login(omit(searchParams, 'iAmEligibleToPlay', 'path'));
    } catch (error) {
      setServerMessage((error as RequestError).body.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const getSecurityQuestions = async () => {
      try {
        setIsLoading(true);
        const response = await lastValueFrom(
          request<Question[]>({
            service: `/api/v1-preview/auth/security-questions`,
            method: 'GET',
            body: {
              userId: searchParams.userId
            }
          })
        );

        if (response.body.length) {
          setQuestions(response.body);
        }
      } finally {
        setIsLoading(false);
      }
    };

    if (searchParams.userId) {
      getSecurityQuestions();
    } else {
      setIsLoading(false);
    }
  }, [request, searchParams.userId]);

  return (
    <Form
      onSubmit={onSubmit}
      render={({ pristine, submitting }) => (
        <>
          {serverMessage && (
            <p className={styles.error}>
              <ServerMessageHandler message={serverMessage} />
            </p>
          )}

          <ErrorMessage />

          <StrongPwdReset data={questions} />

          <Button
            className={styles.submitButton}
            block
            loading={isLoading}
            disabled={pristine || submitting}
            type="submit"
            translation={TXTCLI_Login}
          />

          <Translation
            tagName="p"
            values={{
              a: (msg: React.ReactNode) => (
                <ResolverLink resolverTag={ResolverTag.HelpCenter}>{msg}</ResolverLink>
              )
            }}
            {...TXTCLI_If_you_require_further_assista_ELL_8}
          />
        </>
      )}
    />
  );
};

export default SecurityQuestionsForm;
